import { useState, useEffect, useRef, useMemo } from "react";
import { CallProvider } from "@/contexts/CallContext";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Heart, User, LogOut, Shield, Landmark, Navigation, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { calcDistanceKm } from "@/lib/compatibility";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import NotificationsSheet, { useNotificationCount } from "@/components/NotificationsSheet";
import LowCreditBanner from "@/components/LowCreditBanner";
import { isProfileOnboarding } from "@/lib/profile-onboarding";
import { getInviteDestination, clearInviteDestination } from "@/lib/invite-destination";
import { useUnreadCounts } from "@/shared/hooks/useUnreadCounts";
import { useDatingRequestCount } from "@/shared/hooks/useDatingRequestCount";
import { useProactiveAI } from "@/shared/hooks/useProactiveAI";
import { useProgressierRegistration } from "@/shared/hooks/useProgressierRegistration";

const RELOCATION_COOLDOWN_KEY = "relocation_last_prompt";
const RELOCATION_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const RELOCATION_CONFIRMED_KEY = "relocation_confirmed_coords";
const RELOCATION_DISMISSED_KEY = "relocation_dismissed_gps";

function coordsHash(lat: number, lng: number): string {
  return `${lat.toFixed(2)},${lng.toFixed(2)}`;
}

const BASE_NAV_ITEMS = [
  { icon: MessageCircle, label: "Chats", path: "/chats", datingOnly: false },
  { icon: Heart, label: "Dating", path: "/dating", datingOnly: true },
  { icon: User, label: "Profile", path: "/profile", datingOnly: false },
  { icon: Landmark, label: "Banking", path: "/banking", datingOnly: false },
];

export default function AppShellPhase1() {
  const { user, profile, loading, signOut, isAdmin, refreshProfile, isAuthReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialUnreadCount = useUnreadCounts(user?.id);
  const requestCount = useDatingRequestCount(user?.id);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const liveLocationActive = (profile as any)?.live_location_active ?? false;
  const notifCount = useNotificationCount();
  // Only treat as onboarding when profile is fully loaded (not during hydration)
  const isOnboarding = !!user && isAuthReady && isProfileOnboarding(profile);

  useProactiveAI(user?.id);
  useProgressierRegistration(user?.id);

  const onboardingAllowedRoutes = ["/profile", "/banking", "/premium"];
  useEffect(() => {
    if (!isOnboarding) return;

    // Allow invited users to stay on their invite destination (grace period)
    const inviteDest = getInviteDestination();
    if (inviteDest && location.pathname.startsWith(inviteDest)) return;
    // Clear stale invite destination if user navigated away
    if (inviteDest && !location.pathname.startsWith(inviteDest)) {
      clearInviteDestination();
    }

    if (
      !onboardingAllowedRoutes.includes(location.pathname) &&
      !location.pathname.startsWith("/invite/") &&
      !location.pathname.startsWith("/chat/")
    ) {
      navigate("/profile", { replace: true });
    }
  }, [isOnboarding, location.pathname, navigate]);

  useEffect(() => {
    setUnreadMsgCount(initialUnreadCount);
  }, [initialUnreadCount]);

  const navItems = useMemo(
    () => BASE_NAV_ITEMS.filter((item) => !item.datingOnly || profile?.dating_enabled),
    [profile?.dating_enabled]
  );

  const [relocationData, setRelocationData] = useState<{ lat: number; lng: number; distance: number } | null>(null);
  const [relocationStep, setRelocationStep] = useState<"personal" | "business" | null>(null);
  const [relocationAddress, setRelocationAddress] = useState<string | null>(null);
  const [userBusiness, setUserBusiness] = useState<{ business_name: string } | null>(null);
  const baseCoordsRef = useRef<{ lat: number; lng: number } | null>(null);
  const baseLocationRef = useRef<string | null>(null);
  const relocationShownRef = useRef(false);
  const lastGpsCheckRef = useRef<{ lat: number; lng: number; time: number } | null>(null);

  useEffect(() => {
    if (profile?.latitude && profile?.longitude) {
      baseCoordsRef.current = { lat: profile.latitude, lng: profile.longitude };
    }
    baseLocationRef.current = profile?.location ?? null;
  }, [profile?.latitude, profile?.longitude, profile?.location]);

  useEffect(() => {
    if (!user) return;
    if (!navigator.geolocation) return;

    const startWatch = () => {
      if (!baseCoordsRef.current) return undefined;

      const watchId = navigator.geolocation.watchPosition(
        async (pos) => {
          if (relocationShownRef.current) return;
          try {
            const lastPrompt = localStorage.getItem(RELOCATION_COOLDOWN_KEY);
            if (lastPrompt && Date.now() - parseInt(lastPrompt) < RELOCATION_COOLDOWN_MS) return;
          } catch {}

          const base = baseCoordsRef.current;
          if (!base) return;

          const gpsLat = pos.coords.latitude;
          const gpsLng = pos.coords.longitude;
          const now = Date.now();
          const lastCheck = lastGpsCheckRef.current;

          if (lastCheck && now - lastCheck.time < 30000) return;
          if (lastCheck) {
            const moved = calcDistanceKm(lastCheck.lat, lastCheck.lng, gpsLat, gpsLng);
            if (moved < 0.2) return;
          }
          lastGpsCheckRef.current = { lat: gpsLat, lng: gpsLng, time: now };

          const dist = calcDistanceKm(base.lat, base.lng, gpsLat, gpsLng);
          let countryMismatch = false;
          const storedLocation = baseLocationRef.current;

          if (storedLocation) {
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${gpsLat}&lon=${gpsLng}&format=json&accept-language=en`,
                { headers: { "User-Agent": "WhisprAI/1.0" } }
              );
              const json = await res.json();
              const gpsCountry = json?.address?.country;
              if (gpsCountry && !storedLocation.toLowerCase().includes(gpsCountry.toLowerCase())) {
                countryMismatch = true;
              }
            } catch (error) {
              console.warn("[Relocation] Reverse geocode for country check failed", error);
            }
          }

          if (countryMismatch || dist > 100) {
            const gpsHash = coordsHash(gpsLat, gpsLng);
            const baseHash = coordsHash(base.lat, base.lng);
            try {
              const confirmedCoords = localStorage.getItem(RELOCATION_CONFIRMED_KEY);
              if (confirmedCoords === baseHash) return;
              const dismissedGps = localStorage.getItem(RELOCATION_DISMISSED_KEY);
              if (dismissedGps === gpsHash) return;
            } catch {}

            setRelocationData({ lat: gpsLat, lng: gpsLng, distance: Math.round(dist) });

            try {
              const { data } = await supabase.functions.invoke("places-autocomplete", {
                body: { lat: gpsLat, lng: gpsLng, reverseGeocode: true },
              });
              setRelocationAddress(data?.address ?? `${gpsLat.toFixed(4)}, ${gpsLng.toFixed(4)}`);
            } catch {
              setRelocationAddress(`${gpsLat.toFixed(4)}, ${gpsLng.toFixed(4)}`);
            }

            const { data: biz } = await supabase
              .from("user_businesses")
              .select("business_name")
              .eq("user_id", user.id)
              .eq("is_active", true)
              .maybeSingle();
            setUserBusiness(biz ?? null);

            setRelocationStep("personal");
            relocationShownRef.current = true;
            try {
              localStorage.setItem(RELOCATION_COOLDOWN_KEY, String(Date.now()));
            } catch {}
            navigator.geolocation.clearWatch(watchId);
          }
        },
        (error) => {
          console.warn("[Relocation] Geolocation error:", error.code, error.message);
          if (error.code === 1) {
            toast("Location access denied. Enable it in browser settings to get relevant matches.");
            navigator.geolocation.clearWatch(watchId);
          }
        },
        { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 }
      );
      return watchId;
    };

    let watchId: number | undefined;
    const interval = setInterval(() => {
      if (baseCoordsRef.current && watchId === undefined) {
        watchId = startWatch();
        clearInterval(interval);
      }
    }, 500);

    watchId = startWatch();
    if (watchId !== undefined) clearInterval(interval);

    return () => {
      clearInterval(interval);
      if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
    };
  }, [user]);

  const cleanupRelocation = () => {
    setRelocationData(null);
    setRelocationStep(null);
    setRelocationAddress(null);
    setUserBusiness(null);
    relocationShownRef.current = true;
  };

  const advanceAfterPersonal = () => {
    if (userBusiness) {
      setRelocationStep("business");
    } else {
      cleanupRelocation();
    }
  };

  const handlePersonalConfirm = async () => {
    if (!relocationData || !user) return;
    const locationText = relocationAddress ?? `${relocationData.lat.toFixed(4)}, ${relocationData.lng.toFixed(4)}`;

    const { error } = await supabase
      .from("profiles")
      .update({
        latitude: relocationData.lat,
        longitude: relocationData.lng,
        location: locationText,
        formatted_address: locationText,
      })
      .eq("user_id", user.id);

    if (!error) {
      toast.success(`Personal location updated to ${locationText}!`);
      try {
        localStorage.setItem(RELOCATION_CONFIRMED_KEY, coordsHash(relocationData.lat, relocationData.lng));
        localStorage.removeItem(RELOCATION_DISMISSED_KEY);
      } catch {}
      refreshProfile();

      if (profile?.dating_enabled && profile) {
        supabase.functions
          .invoke("generate-starter-profiles", {
            body: {
              user_id: user.id,
              relocation: true,
              gender_preference: (profile as any).gender_preference ?? [],
              dating_interests: profile.dating_interests ?? [],
              looking_for: profile.looking_for ?? "",
              latitude: relocationData.lat,
              longitude: relocationData.lng,
              location: locationText,
              ethnicity_preference: (profile as any).ethnicity_preference ?? [],
              age_range_min: profile.age_range_min ?? 18,
              age_range_max: profile.age_range_max ?? 99,
              gender: (profile as any).gender ?? "",
              user_age: profile.age ?? null,
            },
          })
          .then(({ error: fnError }) => {
            if (fnError) console.error("Starter profiles error:", fnError);
          });
      }
    } else {
      toast("Failed to update personal location.");
    }

    advanceAfterPersonal();
  };

  const handlePersonalDismiss = () => {
    if (relocationData) {
      try {
        localStorage.setItem(RELOCATION_DISMISSED_KEY, coordsHash(relocationData.lat, relocationData.lng));
      } catch {}
    }
    advanceAfterPersonal();
  };

  const handleBusinessConfirm = async () => {
    if (!relocationData || !user) return;
    const locationText = relocationAddress ?? `${relocationData.lat.toFixed(4)}, ${relocationData.lng.toFixed(4)}`;

    const { error } = await supabase
      .from("user_businesses")
      .update({
        latitude: relocationData.lat,
        longitude: relocationData.lng,
        location: locationText,
      })
      .eq("user_id", user.id);

    if (!error) {
      toast.success(`Business location updated to ${locationText}!`);
    } else {
      toast("Failed to update business location.");
    }

    cleanupRelocation();
  };

  const handleBusinessDismiss = () => {
    cleanupRelocation();
  };

  useEffect(() => {
    if (!user) return;

    const loadUnreadCount = async () => {
      const { data } = await supabase.rpc("get_total_unread_count", { p_user_id: user.id });
      setUnreadMsgCount(typeof data === "number" ? data : 0);
    };

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedLoadUnread = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => loadUnreadCount(), 2000);
    };

    const msgChannel = supabase
      .channel("appshell-unread")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload: any) => {
        if (payload.new?.sender_id && payload.new.sender_id !== user.id) {
          debouncedLoadUnread();
        }
      })
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "conversation_members",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          loadUnreadCount();
        }
      )
      .subscribe();

    const reconcileInterval = setInterval(() => {
      loadUnreadCount();
    }, 30000);

    return () => {
      supabase.removeChannel(msgChannel);
      clearInterval(reconcileInterval);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [user]);

  useEffect(() => {
    if (!user || location.pathname !== "/") return;
    supabase.rpc("get_total_unread_count", { p_user_id: user.id }).then(({ data }) => {
      setUnreadMsgCount(typeof data === "number" ? data : 0);
    });
  }, [location.pathname, user]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  return (
    <CallProvider>
      <div className="fixed inset-0 flex flex-col bg-background overflow-hidden md:flex-row">
        <aside className="hidden md:flex w-[72px] flex-col items-center border-r border-border bg-card py-4 gap-1">
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary">
            <MessageCircle className="h-5 w-5 text-primary-foreground" />
          </div>

          <nav className="flex flex-1 flex-col items-center gap-1">
            {navItems.map((item) => {
              const datingPaths = ["/dating", "/matches", "/leaderboard"];
              const active =
                item.path === "/dating"
                  ? datingPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
                  : location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "relative flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200",
                    active
                      ? "gradient-primary text-primary-foreground shadow-soft"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                  title={item.label}
                >
                  <item.icon className="h-5 w-5" />
                  {item.path === "/" && unreadMsgCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
                      {unreadMsgCount > 99 ? "99+" : unreadMsgCount}
                    </span>
                  )}
                  {item.path === "/dating" && requestCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
                      {requestCount}
                    </span>
                  )}
                  {item.path === "/banking" && notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
                      {notifCount}
                    </span>
                  )}
                  {item.path === "/dating" && liveLocationActive && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                      <span className="animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                  )}
                </button>
              );
            })}

            {isAdmin && (
              <button
                onClick={() => navigate("/admin")}
                className={cn(
                  "relative flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200",
                  location.pathname === "/admin"
                    ? "gradient-primary text-primary-foreground shadow-soft"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
                title="Admin"
              >
                <Shield className="h-5 w-5" />
              </button>
            )}
          </nav>

          <div className="mt-auto flex flex-col items-center gap-2">
            <NotificationsSheet>
              <button
                className="relative flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                title="Notifications"
              >
                <Bell className="h-4 w-4" />
                {notifCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
                    {notifCount}
                  </span>
                )}
              </button>
            </NotificationsSheet>
            <Avatar className="h-9 w-9 cursor-pointer" onClick={() => navigate("/profile")}>
              <AvatarImage src={profile?.avatar_url ?? undefined} />
              <AvatarFallback className="text-xs bg-secondary">
                {profile?.display_name?.charAt(0)?.toUpperCase() ?? "?"}
              </AvatarFallback>
            </Avatar>
            <button
              onClick={() => signOut()}
              className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden overscroll-y-contain min-h-0">
          <div className="flex-1 flex flex-col overflow-hidden min-h-0 w-full max-w-3xl mx-auto">
            <LowCreditBanner />
            <Outlet />
          </div>
        </main>

        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around border-t border-border bg-card/95 backdrop-blur-md px-2 py-1 safe-area-pb">
          {navItems.map((item) => {
            const datingPaths = ["/dating", "/matches", "/leaderboard"];
            const active =
              item.path === "/dating"
                ? datingPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
                : location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
            const isDisabled = isOnboarding && item.path !== "/profile" && item.path !== "/banking";

            return (
              <button
                key={item.path}
                onClick={() => {
                  if (isDisabled) {
                    toast("Complete your profile first");
                    return;
                  }
                  navigate(item.path);
                }}
                className={cn(
                  "flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-2xl transition-all duration-150",
                  isDisabled && "opacity-40 cursor-not-allowed",
                  active && !isDisabled ? "text-primary bg-primary/10" : "text-muted-foreground"
                )}
              >
                <div className="relative">
                  <item.icon className="h-[22px] w-[22px]" />
                  {!isDisabled && item.path === "/" && unreadMsgCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground px-1">
                      {unreadMsgCount > 99 ? "99+" : unreadMsgCount}
                    </span>
                  )}
                  {!isDisabled && item.path === "/dating" && requestCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground px-1">
                      {requestCount}
                    </span>
                  )}
                  {!isDisabled && item.path === "/banking" && notifCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground px-1">
                      {notifCount}
                    </span>
                  )}
                  {item.path === "/dating" && liveLocationActive && (
                    <span className="absolute -top-1 -right-1.5 flex h-2 w-2">
                      <span className="animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-medium">{item.label}</span>
              </button>
            );
          })}

          {!isOnboarding && (
            <NotificationsSheet>
              <button className="flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-2xl transition-all duration-150 text-muted-foreground relative">
                <div className="relative">
                  <Bell className="h-[22px] w-[22px]" />
                  {notifCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground px-1">
                      {notifCount}
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-medium">Alerts</span>
              </button>
            </NotificationsSheet>
          )}

          {isAdmin && !isOnboarding && (
            <button
              onClick={() => navigate("/admin")}
              className={cn(
                "flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-2xl transition-all duration-150",
                location.pathname === "/admin" ? "text-primary bg-primary/10" : "text-muted-foreground"
              )}
            >
              <Shield className="h-[22px] w-[22px]" />
              <span className="text-[11px] font-medium">Admin</span>
            </button>
          )}
        </nav>

        <AlertDialog open={relocationStep === "personal"} onOpenChange={(open) => { if (!open) handlePersonalDismiss(); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Navigation className="h-5 w-5 text-primary" />
                Update Personal Location?
              </AlertDialogTitle>
              <AlertDialogDescription>
                You're approximately {relocationData?.distance ? `${relocationData.distance.toLocaleString()}km` : "far"} from your saved location{profile?.location ? ` in ${profile.location}` : ""}. Would you like to update your personal location{relocationAddress ? ` to ${relocationAddress}` : ""}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handlePersonalDismiss}>Keep Current</AlertDialogCancel>
              <AlertDialogAction onClick={handlePersonalConfirm}>Update Location</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={relocationStep === "business"} onOpenChange={(open) => { if (!open) handleBusinessDismiss(); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Navigation className="h-5 w-5 text-primary" />
                Update Business Location?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Would you like to update your business location{userBusiness ? ` for ${userBusiness.business_name}` : ""}{relocationAddress ? ` to ${relocationAddress}` : ""}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleBusinessDismiss}>Keep Current</AlertDialogCancel>
              <AlertDialogAction onClick={handleBusinessConfirm}>Update Location</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </CallProvider>
  );
}
